import {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, ScrollView, View} from 'react-native';
import {Button, ProgressBar} from 'react-native-paper';
import {Form, FormHandle} from '../../Utils/SimpleForm';
import Question from './Question/Question';
import {Text} from '../../Components';
import {PageContainer} from '../../Containers';
import {useBackHandler, useTheme} from '../../Hooks';
import {useConsultation} from '../../Hooks/useConsultation';
import QuestionsJSON from '../../Models/questions';
import {QuestionId} from '../../Models/questionsI18n';
import {localizedModuleTitle} from '../../Models/moduleTitles.i18n';
import {
  AssessmentLanguageCode,
  AssessmentSessionSnapshot,
  buildAssessmentAnswer,
  buildResultSummaryFromVerdicts,
  clearActiveAssessment,
  createInitialSession,
  loadActiveAssessment,
  saveActiveAssessment,
  saveCompletedAssessment,
  upsertAnswer,
} from '../../Utils/Assessment';
import {isClinicianGuidedAssessment} from '../../Utils/ClinicalResults';
import {getCopy} from '../../Utils/I18n';
import {recordSession, trackEvent} from '../../Utils/Telemetry';
import {RootQuestionsScreenProps} from '../../Typings/NavigationTypes';
import GlobalStyles from '../../Styles/GlobalStyles';
import getStyles from './Questions.styles';

type Props = RootQuestionsScreenProps;
type QuestionKey = keyof typeof QuestionsJSON;

// The Question form stores the choice *key* ("0"/"1"/…); the engine matches on
// the canonical English label, so map key → label via questions.json.
const labelForChoice = (feature: string, choiceKey: string): string => {
  const question = QuestionsJSON[feature as QuestionKey];
  return question?.choices?.[choiceKey] ?? choiceKey;
};

const Questions: React.FC<Props> = ({navigation, route}) => {
  const formRef = useRef<FormHandle>(null);
  const theme = useTheme();
  const styles = getStyles(theme);

  const routeLanguage = (route.params?.language || 'en') as AssessmentLanguageCode;
  const routeAssessmentMode = route.params?.assessmentMode || 'self-check';
  const routeStartedByProvider = Boolean(route.params?.startedByProvider);
  const routeSharingConsent = route.params?.sharingConsent;
  const routeParticipantAlias = route.params?.participantAlias;

  const [sessionSnapshot, setSessionSnapshot] = useState<AssessmentSessionSnapshot>(() =>
    createInitialSession(routeLanguage, {
      mode: routeAssessmentMode,
      startedByProvider: routeStartedByProvider,
      sharingConsent: routeSharingConsent,
      participantAlias: routeParticipantAlias,
    }),
  );
  const sessionSnapshotRef = useRef(sessionSnapshot);
  const isLeavingWithoutPromptRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const [isRestoring, setIsRestoring] = useState(Boolean(route.params?.resume));

  const consultation = useConsultation();
  const copy = getCopy(sessionSnapshot.language);
  const {current, answeredCount, observed} = consultation;

  const persistSession = useCallback(async (next: AssessmentSessionSnapshot) => {
    sessionSnapshotRef.current = next;
    setSessionSnapshot(next);
    await saveActiveAssessment(next);
  }, []);

  const saveAndLeaveAssessment = useCallback(
    async (leavePage?: () => void) => {
      const snap = sessionSnapshotRef.current;
      await saveActiveAssessment(snap);
      recordSession(snap);
      trackEvent('screening_paused', {
        sessionId: snap.sessionId,
        language: snap.language,
        payload: {questionCount: snap.answers.length},
      });
      isLeavingWithoutPromptRef.current = true;
      if (leavePage) {
        leavePage();
        return;
      }
      navigation.navigate('Landing');
    },
    [navigation],
  );

  const onPausePress = useCallback(() => {
    Alert.alert(copy.questions.pauseTitle, copy.questions.pauseMessage, [
      {text: copy.questions.saveAndLeave, onPress: () => saveAndLeaveAssessment()},
      {text: copy.questions.stay, style: 'cancel'},
    ]);
  }, [
    copy.questions.pauseMessage,
    copy.questions.pauseTitle,
    copy.questions.saveAndLeave,
    copy.questions.stay,
    saveAndLeaveAssessment,
  ]);

  const goBackOneQuestion = useCallback(async () => {
    const snap = sessionSnapshotRef.current;
    if (snap.answers.length === 0) {
      return;
    }

    const previousAnswers = snap.answers.slice(0, -1);
    const removedAnswer = snap.answers[snap.answers.length - 1];
    const nextSnapshot: AssessmentSessionSnapshot = {
      ...snap,
      answers: previousAnswers,
      answeredQuestions: previousAnswers.map((answer) => answer.questionId),
      selectedQuestion: 'notSelected',
      updatedAt: new Date().toISOString(),
    };

    formRef.current?.changeValue(removedAnswer.questionId, undefined);
    consultation.reset(
      previousAnswers.map((answer) => ({
        questionId: answer.questionId,
        answerLabel: answer.answerLabel,
      })),
    );
    await persistSession(nextSnapshot);
    recordSession(nextSnapshot);
    trackEvent('question_back', {
      sessionId: snap.sessionId,
      language: snap.language,
      payload: {questionId: removedAnswer.questionId, position: previousAnswers.length},
    });
  }, [consultation, persistSession]);

  const shouldHandleQuestionBack = useCallback(
    () => !isLeavingWithoutPromptRef.current && sessionSnapshotRef.current.answers.length > 0,
    [],
  );

  useBackHandler(
    navigation,
    () => {
      goBackOneQuestion();
    },
    shouldHandleQuestionBack,
  );

  // Initialise (fresh) or resume a saved consultation. Runs once on mount.
  useEffect(() => {
    let canceled = false;
    const initialize = async () => {
      if (route.params?.resume) {
        const saved = await loadActiveAssessment();
        if (saved && !canceled) {
          sessionSnapshotRef.current = saved;
          setSessionSnapshot(saved);
          consultation.reset(
            saved.answers.map((a) => ({questionId: a.questionId, answerLabel: a.answerLabel})),
          );
          setIsRestoring(false);
          trackEvent('screening_resumed', {
            sessionId: saved.sessionId,
            language: saved.language,
            payload: {questionCount: saved.answers.length},
          });
          return;
        }
      }
      if (route.params?.reset) {
        await clearActiveAssessment();
      }
      await saveActiveAssessment(sessionSnapshotRef.current);
      setIsRestoring(false);
      const fresh = sessionSnapshotRef.current;
      trackEvent('screening_started', {
        sessionId: fresh.sessionId,
        language: fresh.language,
        payload: {mode: fresh.mode, startedByProvider: Boolean(fresh.startedByProvider)},
      });
    };
    initialize();
    return () => {
      canceled = true;
    };
  }, []);

  const completeAssessment = useCallback(async () => {
    const snap = sessionSnapshotRef.current;
    const summary = buildResultSummaryFromVerdicts(
      snap.sessionId,
      consultation.results,
      snap.language,
    );
    const completed: AssessmentSessionSnapshot = {
      ...snap,
      status: 'completed',
      selectedQuestion: 'notSelected',
      predictions: summary.predictions,
      resultSummary: summary,
      updatedAt: summary.completedAt,
    };
    await saveCompletedAssessment(completed, summary);
    sessionSnapshotRef.current = completed;
    setSessionSnapshot(completed);
    isLeavingWithoutPromptRef.current = true;
    recordSession(completed, summary);
    trackEvent('screening_completed', {
      sessionId: completed.sessionId,
      language: completed.language,
      payload: {questionCount: completed.answers.length, severityLevel: summary.severityLevel},
    });
    navigation.replace('Results', {
      sessionId: summary.sessionId,
      summary,
      language: snap.language,
      clinical: isClinicianGuidedAssessment(snap),
    });
  }, [consultation.results, navigation]);

  // When the engine determines all four modules, finalise and navigate.
  useEffect(() => {
    if (consultation.isComplete && !isRestoring && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      completeAssessment();
    }
  }, [consultation.isComplete, isRestoring, completeAssessment]);

  const onContinue = useCallback(async () => {
    if (!current) {
      return;
    }
    const raw = formRef.current?.getValue(current.feature);
    if (raw === undefined || raw === null || raw === '') {
      Alert.alert(copy.questions.chooseAnswer);
      return;
    }
    const choiceKey = String(raw);
    const label = labelForChoice(current.feature, choiceKey);
    try {
      const answer = buildAssessmentAnswer(current.feature as QuestionKey, choiceKey);
      const snap = sessionSnapshotRef.current;
      const nextAnswers = upsertAnswer(snap.answers, answer);
      const nextAnswered = Array.from(
        new Set<string>([...snap.answeredQuestions, current.feature]),
      );
      const nextSnapshot: AssessmentSessionSnapshot = {
        ...snap,
        answers: nextAnswers,
        answeredQuestions: nextAnswered,
        updatedAt: new Date().toISOString(),
      };
      await persistSession(nextSnapshot);
      // Keep the shared copy of the session current after every answer, so an
      // abandoned screening is still visible for drop-off analysis.
      recordSession(nextSnapshot);
      trackEvent('question_answered', {
        sessionId: snap.sessionId,
        language: snap.language,
        payload: {
          questionId: current.feature,
          position: nextAnswers.length,
          module: current.moduleTarget,
        },
      });
      consultation.submitAnswer(current.feature, label);
    } catch (error) {
      console.error('Failed to record answer', error);
      Alert.alert(copy.questions.failedAnswer);
    }
  }, [
    consultation,
    copy.questions.chooseAnswer,
    copy.questions.failedAnswer,
    current,
    persistSession,
  ]);

  let questionContent: JSX.Element;
  if (isRestoring) {
    questionContent = (
      <View style={styles.questionCard}>
        <Text weight="semiBold" style={styles.strongBody}>
          {copy.questions.restoring}
        </Text>
        <Text style={styles.privacyReminder}>{copy.questions.privacy}</Text>
      </View>
    );
  } else if (current) {
    // Progress reflects whole modules *completed* (a fixed set of 4), not a
    // per-question fraction — the question count varies with the answers.
    const moduleProgress =
      current.moduleCount > 0 ? (current.moduleIndex - 1) / current.moduleCount : 0;
    questionContent = (
      <View style={styles.questionCard}>
        <View style={styles.progressHeader}>
          <Text weight="semiBold" style={styles.progressText}>
            {localizedModuleTitle(
              current.moduleTarget,
              sessionSnapshot.language,
              current.moduleTitle,
            )}
          </Text>
          <Text style={styles.progressText}>
            {answeredCount > 0
              ? `${answeredCount} ${copy.questions.answered}`
              : copy.questions.adaptive}
          </Text>
        </View>
        <ProgressBar
          progress={moduleProgress}
          color={theme.colors.primary}
          style={styles.progressBar}
        />

        <Text style={styles.privacyReminder}>{copy.questions.progressNote}</Text>

        <Question
          id={current.feature as QuestionId}
          language={sessionSnapshot.language}
          moduleTarget={current.moduleTarget}
          observed={observed}
        />

        <Button
          mode="contained"
          onPress={onContinue}
          style={styles.submitButton}
          buttonColor={theme.colors.primary}
          textColor={theme.colors.textContrast}
        >
          {copy.questions.continue}
        </Button>

        <Button
          mode="text"
          onPress={onPausePress}
          style={styles.pauseButton}
          textColor={theme.colors.primary}
        >
          {copy.questions.pause}
        </Button>
      </View>
    );
  } else {
    questionContent = (
      <View style={styles.questionCard}>
        <Text weight="semiBold" style={styles.strongBody}>
          {copy.questions.enoughTitle}
        </Text>
        <Text style={styles.privacyReminder}>{copy.questions.enoughBody}</Text>
      </View>
    );
  }

  return (
    <View style={GlobalStyles.flex1}>
      <PageContainer withPadding withSafeArea edges={['bottom', 'left', 'right']}>
        <Form ref={formRef} initialState={{}}>
          <ScrollView contentContainerStyle={styles.container}>{questionContent}</ScrollView>
        </Form>
      </PageContainer>
    </View>
  );
};

export default Questions;
